import { useCallback, useMemo } from 'react';
import { MooAnalyticsEvents } from '@cowbell/data/analytics';
import type { Context } from '../integrations/context-types';
import { useMooChatContext, useMooConfig } from './internal-context';
import type {
  LayoutMode,
  NextPromptSuggestion,
  PaneId,
} from '../state/shell-state';
import {
  selectAgents,
  selectSelection,
  selectDrawer,
  selectLauncher,
  selectActivePane,
  selectContexts,
  selectDismissedDerived,
  selectError,
  selectStorageHydrated,
  selectLayoutMode,
} from '../state/selectors';

/** Strips the client-only `pinned` flag from a Context before it crosses the
 *  transport boundary. Architecture rule: `pinned` never leaves the client. */
function stripPinned(c: Context): Context {
  if (!('pinned' in c)) return c;
  const { pinned: _pinned, ...rest } = c;
  return rest;
}

export function useShellAgents() {
  const { state } = useMooChatContext();
  return selectAgents(state);
}

export function useShellSelection() {
  const { state, dispatch } = useMooChatContext();
  const { logger, analytics } = useMooConfig();
  const selectAgent = useCallback(
    /** Switch the active agent. When `replayQuery` is set (e.g. the
     *  specialist-switch navigation card accepts a recommendation), the
     *  original question is queued in `pendingReplay`; `ChatPanel`
     *  picks it up on the next render and auto-sends it to the new
     *  agent so the user doesn't have to retype.
     *
     *  Any switch *without* a replayQuery (sidebar / agent picker /
     *  default-agent auto-select) clears a stale queued replay so a
     *  previously-queued question can never auto-send to a
     *  manually-selected agent. V1 parity (AskMooProvider comment). */
    (agentId: string, replayQuery?: string) => {
      dispatch({ type: 'AGENT_SELECTED', agentId });
      if (replayQuery && replayQuery.trim().length > 0) {
        dispatch({ type: 'MESSAGE_REPLAY_QUEUED', text: replayQuery });
      } else {
        dispatch({ type: 'MESSAGE_REPLAY_CLEARED' });
      }
      logger.debug('moo.agent.selected', { agentId });
      analytics.track(MooAnalyticsEvents.AGENT_SELECTED, { agentId });
    },
    [dispatch, logger, analytics]
  );
  const selectConversation = useCallback(
    (conversationId: string | null) =>
      dispatch({ type: 'CONVERSATION_SELECTED', conversationId }),
    [dispatch]
  );
  const newConversation = useCallback(() => {
    dispatch({ type: 'CONVERSATION_NEW_REQUESTED' });
    logger.debug('moo.conversation.new');
    analytics.track(MooAnalyticsEvents.CONVERSATION_NEW, {});
  }, [dispatch, logger, analytics]);
  const openConversation = useCallback(
    (conversationId: string, agentId: string) => {
      dispatch({ type: 'CONVERSATION_OPENED', conversationId, agentId });
      logger.debug('moo.conversation.opened', { conversationId, agentId });
      analytics.track(MooAnalyticsEvents.CONVERSATION_OPENED, {
        conversationId,
        agentId,
      });
    },
    [dispatch, logger, analytics]
  );
  const selection = selectSelection(state);
  return {
    agentId: selection.agentId,
    conversationId: selection.conversationId,
    resetNonce: selection.resetNonce,
    selectAgent,
    selectConversation,
    newConversation,
    openConversation,
  };
}

export function useShellDrawer() {
  const { state, dispatch } = useMooChatContext();
  const drawer = selectDrawer(state);
  const open = useCallback(
    () => dispatch({ type: 'DRAWER_OPENED' }),
    [dispatch]
  );
  const close = useCallback(
    () => dispatch({ type: 'DRAWER_CLOSED' }),
    [dispatch]
  );
  const setView = useCallback(
    (view: 'default' | 'all-agents' | 'all-conversations') =>
      dispatch({ type: 'DRAWER_VIEW_CHANGED', view }),
    [dispatch]
  );
  const setPinned = useCallback(
    (pinned: boolean) => dispatch({ type: 'DRAWER_PINNED', pinned }),
    [dispatch]
  );
  return {
    isOpen: drawer.isOpen,
    view: drawer.view,
    pinned: drawer.pinned,
    open,
    close,
    setView,
    setPinned,
  };
}

export function useShellLauncher() {
  const { state, dispatch } = useMooChatContext();
  const launcher = selectLauncher(state);
  const open = useCallback(
    () => dispatch({ type: 'LAUNCHER_OPENED' }),
    [dispatch]
  );
  const close = useCallback(
    () => dispatch({ type: 'LAUNCHER_CLOSED' }),
    [dispatch]
  );
  const toggle = useCallback(
    () => dispatch({ type: 'LAUNCHER_TOGGLED' }),
    [dispatch]
  );
  return { isOpen: launcher.isOpen, open, close, toggle };
}

export function useShellPane(paneId: PaneId = 'main') {
  const { state, dispatch } = useMooChatContext();
  const pane = selectActivePane(state, paneId);
  const setComposerText = useCallback(
    (text: string) =>
      dispatch({ type: 'PANE_COMPOSER_TEXT_CHANGED', paneId, text }),
    [dispatch, paneId]
  );
  const setToneId = useCallback(
    (toneId: string | null) =>
      dispatch({ type: 'PANE_TONE_CHANGED', paneId, toneId }),
    [dispatch, paneId]
  );
  const setScrollPositionPx = useCallback(
    (px: number) =>
      dispatch({ type: 'PANE_SCROLL_POSITION_CHANGED', paneId, px }),
    [dispatch, paneId]
  );
  const setWebSearchEnabled = useCallback(
    (enabled: boolean) =>
      dispatch({ type: 'PANE_WEB_SEARCH_CHANGED', paneId, enabled }),
    [dispatch, paneId]
  );
  const requestNextPrompt = useCallback(
    (requestId: string) =>
      dispatch({ type: 'PANE_NEXT_PROMPT_REQUESTED', paneId, requestId }),
    [dispatch, paneId]
  );
  const receiveNextPrompt = useCallback(
    (requestId: string, suggestion: NextPromptSuggestion | null) =>
      dispatch({
        type: 'PANE_NEXT_PROMPT_RECEIVED',
        paneId,
        requestId,
        suggestion,
      }),
    [dispatch, paneId]
  );
  const clearNextPrompt = useCallback(
    () => dispatch({ type: 'PANE_NEXT_PROMPT_CLEARED', paneId }),
    [dispatch, paneId]
  );
  return {
    agentId: pane?.agentId ?? null,
    conversationId: pane?.conversationId ?? null,
    composerText: pane?.composerText ?? '',
    nextPrompt: pane?.nextPrompt?.suggestion ?? null,
    requestNextPrompt,
    receiveNextPrompt,
    clearNextPrompt,
    toneId: pane?.toneId ?? null,
    webSearchEnabled: pane?.webSearchEnabled ?? true,
    scrollPositionPx: pane?.scrollPositionPx ?? 0,
    setComposerText,
    setToneId,
    setWebSearchEnabled,
    setScrollPositionPx,
  };
}

export function useShellContexts() {
  const { state, dispatch } = useMooChatContext();
  const { logger, analytics } = useMooConfig();
  const contexts = selectContexts(state);
  // `merged` is the transport-facing view: `pinned` is stripped here so
  // every transport consumer (chat stream, improve-prompt, starters) is
  // safe by default. Architecture rule: `pinned` is client-only.
  const merged = useMemo(
    () => [...contexts.user, ...contexts.derived].map(stripPinned),
    [contexts.user, contexts.derived]
  );
  const attach = useCallback(
    (context: Context) => {
      dispatch({ type: 'CONTEXT_USER_ATTACHED', context });
      logger.debug('moo.context.attached', { contextType: context.kind });
      analytics.track(MooAnalyticsEvents.CONTEXT_ATTACHED, {
        contextType: context.kind,
      });
    },
    [dispatch, logger, analytics]
  );
  const remove = useCallback(
    (index: number) => {
      const removed = contexts.user[index];
      dispatch({ type: 'CONTEXT_USER_REMOVED', index });
      if (!removed) return;
      logger.debug('moo.context.removed', { contextType: removed.kind });
      analytics.track(MooAnalyticsEvents.CONTEXT_REMOVED, {
        contextType: removed.kind,
      });
    },
    [dispatch, contexts.user, logger, analytics]
  );
  const pin = useCallback(
    (index: number) => {
      const target = contexts.user[index];
      if (!target) return;
      // V1 parity: only entity / screen chips are pinnable. Selection and
      // document chips are transient by design (their value is bound to a
      // specific message turn), so pinning them across New Chat or sends
      // would surface stale content.
      if (target.kind !== 'entity' && target.kind !== 'screen') return;
      dispatch({ type: 'CONTEXT_PINNED', index });
      logger.debug('moo.context.pinned', { contextType: target.kind });
      analytics.track(MooAnalyticsEvents.CONTEXT_PINNED, {
        contextType: target.kind,
      });
    },
    [dispatch, contexts.user, logger, analytics]
  );
  const unpin = useCallback(
    (index: number) => {
      const target = contexts.user[index];
      if (!target) return;
      if (target.kind !== 'entity' && target.kind !== 'screen') return;
      dispatch({ type: 'CONTEXT_UNPINNED', index });
      logger.debug('moo.context.unpinned', { contextType: target.kind });
      analytics.track(MooAnalyticsEvents.CONTEXT_UNPINNED, {
        contextType: target.kind,
      });
    },
    [dispatch, contexts.user, logger, analytics]
  );
  const setDerived = useCallback(
    /** `routeSignature` opts the call site into per-route dismissal
     *  memory: once the user closes a derived chip, subsequent
     *  setDerived calls on the *same* signature will filter that chip
     *  out. Omit to keep V1 behavior (no dismissal memory). */
    (next: Context[], routeSignature?: string) =>
      dispatch({
        type: 'CONTEXT_DERIVED_SET',
        contexts: next,
        ...(routeSignature === undefined ? {} : { routeSignature }),
      }),
    [dispatch]
  );
  const pinDerived = useCallback(
    (index: number) => {
      const target = contexts.derived[index];
      if (!target) return;
      // Same pinnability rule as user chips — entity + screen only.
      if (target.kind !== 'entity' && target.kind !== 'screen') return;
      // Pin-to-promote (slice 2c decision log, 2026-06-12): pinning a
      // derived chip changes its ownership. The server sees a
      // user-attached context (not auto-derived); the derived lane
      // surrenders the entry so it doesn't double-render.
      dispatch({
        type: 'CONTEXT_USER_ATTACHED',
        context: { ...target, pinned: true },
      });
      dispatch({ type: 'CONTEXT_DERIVED_REMOVED', index });
      logger.debug('moo.context.derived.promoted', {
        contextType: target.kind,
      });
      analytics.track(MooAnalyticsEvents.CONTEXT_PINNED, {
        contextType: target.kind,
      });
    },
    [dispatch, contexts.derived, logger, analytics]
  );
  const unpinDerived = useCallback(
    (index: number) => {
      const target = contexts.derived[index];
      if (!target) return;
      if (target.kind !== 'entity' && target.kind !== 'screen') return;
      dispatch({ type: 'CONTEXT_DERIVED_UNPINNED', index });
      logger.debug('moo.context.derived.unpinned', {
        contextType: target.kind,
      });
      analytics.track(MooAnalyticsEvents.CONTEXT_UNPINNED, {
        contextType: target.kind,
      });
    },
    [dispatch, contexts.derived, logger, analytics]
  );
  const dismissedDerived = useMemo(
    () => selectDismissedDerived({ contexts }),
    [contexts]
  );
  const restoreDerived = useCallback(
    (context: Context) => {
      dispatch({ type: 'CONTEXT_DERIVED_RESTORED', context });
      logger.debug('moo.context.derived.restored', {
        contextType: context.kind,
      });
      analytics.track(MooAnalyticsEvents.CONTEXT_ATTACHED, {
        contextType: context.kind,
      });
    },
    [dispatch, logger, analytics]
  );
  const removeDerived = useCallback(
    (index: number) => {
      const removed = contexts.derived[index];
      dispatch({ type: 'CONTEXT_DERIVED_REMOVED', index });
      if (!removed) return;
      logger.debug('moo.context.derived.removed', {
        contextType: removed.kind,
      });
      analytics.track(MooAnalyticsEvents.CONTEXT_REMOVED, {
        contextType: removed.kind,
      });
    },
    [dispatch, contexts.derived, logger, analytics]
  );
  return {
    user: contexts.user,
    derived: contexts.derived,
    merged,
    attach,
    remove,
    pin,
    unpin,
    setDerived,
    pinDerived,
    unpinDerived,
    removeDerived,
    dismissedDerived,
    restoreDerived,
  };
}

/** True once the library's storage hydration phase has run (either it
 *  finished reading the host's StorageAdapter, or no adapter was
 *  configured). Hosts gate any auto-select-default-agent effect on this
 *  so a default dispatch doesn't race the restored session selection. */
export function useShellStorageHydrated(): boolean {
  const { state } = useMooChatContext();
  return selectStorageHydrated(state);
}

export function useShellLayout() {
  const { state, dispatch } = useMooChatContext();
  const setMode = useCallback(
    (mode: LayoutMode) => dispatch({ type: 'LAYOUT_MODE_CHANGED', mode }),
    [dispatch]
  );
  return { mode: selectLayoutMode(state), setMode };
}

export function useShellError(conversationKey: string) {
  const { state, dispatch } = useMooChatContext();
  const error = selectError(state, conversationKey);
  const set = useCallback(
    (message: string) =>
      dispatch({
        type: 'STREAM_ERROR_OCCURRED',
        conversationKey,
        error: { message, at: new Date().toISOString() },
      }),
    [dispatch, conversationKey]
  );
  const clear = useCallback(
    () => dispatch({ type: 'STREAM_ERROR_CLEARED', conversationKey }),
    [dispatch, conversationKey]
  );
  return { error, set, clear };
}
